import { UnwrapPromise } from "@prisma/client";
import { format, getDayOfYear, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { Fragment } from "react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiExternalLink,
  FiInfo,
} from "react-icons/fi";
import {
  Link,
  LoaderFunction,
  MetaFunction,
  Outlet,
  useLoaderData,
  useMatches,
} from "remix";
import invariant from "tiny-invariant";
import { BreadcrumbLink } from "~/components/Breadcrumbs";
import {
  ButtonHyperLink,
  ButtonLink,
  EditButtonLink,
} from "~/components/Button";
import { Category } from "~/components/Category";
import { ContentBlock } from "~/components/ContentBlock";
import { EventLink } from "~/components/EventLink";
import { Human } from "~/components/Human";
import { IconLiveStream } from "~/components/IconLiveStream";
import { ImageSlider } from "~/components/ImageSlider";
import { ContentBlockModel, getContentBlock } from "~/utils/contentBlocks";
import { db } from "~/utils/db.server";
import { requireFestival } from "~/utils/festival.server";
import { useFestival } from "~/utils/useFestival";
import { useIsLoggedIn } from "~/utils/useIsLoggedIn";
import { youtubeUrltoEmbed } from "~/utils/youtubeUrltoEmbed";
import { LoaderData as EventsLoaderData } from "../events";

interface LoaderData {
  event: UnwrapPromise<ReturnType<typeof loadEvent>>;
  nextEvent: UnwrapPromise<ReturnType<typeof loadSibling>>;
  prevEvent: UnwrapPromise<ReturnType<typeof loadSibling>>;
  externalEventBlock: ContentBlockModel;
}

function loadEvent(slug: string, festivalId: string) {
  return db.event.findFirst({
    where: { slug, festivalId },
    include: {
      categories: true,
      images: true,
      humans: {
        include: {
          image: true,
        },
      },
    },
  });
}

async function loadSibling(
  id: string,
  festivalId: string,
  from: Date,
  direction: "prev" | "next"
) {
  const nearSiblings = await db.event.findMany({
    where: {
      festivalId,
      from: direction === "prev" ? { lte: from } : { gte: from },
    },

    orderBy: [
      {
        from: "asc",
      },
      {
        title: "asc",
      },
    ],
  });
  const indexOfId = nearSiblings.findIndex((event) => event.id === id);

  if (indexOfId >= 0) {
    if (direction === "prev") {
      return nearSiblings[indexOfId - 1] ?? null;
    } else {
      return nearSiblings[indexOfId + 1] ?? null;
    }
  }
  return null;
}

export const meta: MetaFunction = ({ data }) => {
  return {
    title: data?.event?.title,
    description: data?.event?.summary?.substring(0, 140),
  };
};

export const loader: LoaderFunction = async ({
  params,
  request,
  ...rest
}): Promise<LoaderData> => {
  invariant(typeof params.festivalSlug === "string", "festivalId is required");
  invariant(typeof params.eventSlug === "string", "eventSlug is required");

  const [festival, externalEventBlock] = await Promise.all([
    requireFestival(params.festivalSlug),
    getContentBlock("/event/external"),
  ]);

  const event = await loadEvent(params.eventSlug, festival.id);

  if (!event) {
    throw new Response("Not Found", {
      status: 404,
    });
  }

  const [nextEvent, prevEvent] = await Promise.all([
    loadSibling(event.id, festival.id, event.from, "next"),
    loadSibling(event.id, festival.id, event.from, "prev"),
  ]);

  return {
    event,
    nextEvent,
    prevEvent,
    externalEventBlock,
  };
};

export const handle = {
  breadcrumb: ({ data }: { data: LoaderData }) => {
    return data?.event?.title ? (
      <BreadcrumbLink to={`${data?.event?.slug}`}>
        {data?.event?.title}
      </BreadcrumbLink>
    ) : (
      <>…</>
    );
  },
};

export function CatchBoundary() {
  return (
    <div className="p-5">
      <h2>We couldn't find that event!</h2>
    </div>
  );
}

export default function EventSlug() {
  const { event, nextEvent, prevEvent, externalEventBlock } =
    useLoaderData<LoaderData>();
  const matches = useMatches();
  const isLoggedIn = useIsLoggedIn();

  const festival = useFestival();

  const eventsLoaderData = matches.find((match) => !!match.data?.events)
    ?.data as EventsLoaderData;

  invariant(eventsLoaderData.events, "Events are required");
  invariant(event, "Event is required");

  let currentDayOfYear: number | null = null;

  const fromDate = parseISO(event.from.toString());
  const toDate = parseISO(event.to.toString());

  const eventIsInPast = toDate < new Date();

  return (
    <div className="w-full flex flex-col md:flex-row">
      <article className="my-10 md:flex-grow">
        {!!event.embedUrl && (
          <div className="bg-black mb-8 text-white aspect-video flex justify-center items-center">
            <iframe
              width="100%"
              className="aspect-video"
              src={youtubeUrltoEmbed(event.embedUrl)}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        <h1 className="text-3xl md:text-5xl my-2 font-bold">{event.title}</h1>

        <p className="text-xl md:text-2xl my-2 text-primary-500">
          {format(fromDate, "dd.MM.yyyy", { locale: de })}
          {" · "}
          <span className="text-primary-500">
            {format(fromDate, "HH:mm", { locale: de })}
            {" – "}
            {format(toDate, "HH:mm", { locale: de })}
            {"Uhr"}
          </span>
        </p>

        {!!event.location && <p className="text-gray-700">{event.location}</p>}

        {(event.categories.length > 0 || event.hybrid) && (
          <ul className="my-4 flex flex-row flex-wrap gap-2">
            {event.hybrid && (
              <li key="hybrid">
                <span className="inline-flex flex-row flex-nowrap gap-2 items-center shadow-sm bg-white text-primary-500 border border-primary-500 text-md md:text-lg px-2 rounded-md">
                  <IconLiveStream /> Hybrid Veranstaltung
                </span>
              </li>
            )}
            {event.categories.map((category) => (
              <li key={category.id}>
                <Category category={category} />
              </li>
            ))}
          </ul>
        )}

        {event.images.length > 0 && (
          <>
            <hr className=" pt-5 mt-5 border-t border-primary-100" />
            <ImageSlider images={event.images} />
          </>
        )}

        {event.summary && (
          <>
            <hr className=" pt-5 mt-5 border-t border-primary-100" />
            <div
              className="prose-sm md:prose lg:prose-lg"
              dangerouslySetInnerHTML={{ __html: event.summary }}
            />
          </>
        )}

        {event.external && (
          <div className="text-lg my-8 p-4 bg-gray-100 text-gray-700 border-2 border-gray-200 rounded-md">
            <ContentBlock block={externalEventBlock} editable={isLoggedIn} />
          </div>
        )}

        {event.link &&
          event.linkText &&
          (eventIsInPast ? (
            <div className="inline-flex items-center  my-5 p-4 bg-gray-100 text-gray-700 border-2 border-gray-200 rounded-md">
              <FiInfo className="mr-2 text-2xl" />
              Die Veranstaltung hat bereits stattgefunden. Eine Anmeldung ist
              nicht mehr möglich.
            </div>
          ) : (
            <ButtonHyperLink
              className="my-5"
              href={event.link}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              {event.linkText}
              <FiExternalLink className="ml-2" />
            </ButtonHyperLink>
          ))}

        {event.humans.length > 0 && (
          <Fragment>
            <ul className="mt-10 pt-32 pb-10 list-reset grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-t-primary-100">
              {event.humans.map((human) => (
                <li key={human.id} className="mb-24">
                  <Human {...human} />
                </li>
              ))}
            </ul>
          </Fragment>
        )}

        <Outlet />

        {isLoggedIn && (
          <div className="my-4">
            <EditButtonLink className="my-5" to="edit">
              Edit Event
            </EditButtonLink>
          </div>
        )}

        {(prevEvent || nextEvent) && (
          <div className="flex flex-col md:flex-row justify-between my-8 pt-3 border-t border-t-primary-100">
            {prevEvent && (
              <Link
                className="md:max-w-sm p-3 rounded-md border-2 border-transparent hover:border-primary-100 text-lg text-primary-600 hover:text-primary-800"
                to={`/${festival?.slug}/events/${prevEvent.slug}`}
              >
                <span className="text-sm font-bold flex flex-row items-center">
                  <FiArrowLeft className="mr-2" /> Davor
                </span>
                <span className="block overflow-hidden text-ellipsis md:whitespace-nowrap text-gray-800 text-sm md:text-md">
                  {prevEvent.title}
                </span>
              </Link>
            )}
            {nextEvent && (
              <Link
                className="md:max-w-sm p-3 md:ml-auto rounded-md border-2 border-transparent hover:border-primary-100 text-lg text-primary-600 hover:text-primary-800"
                to={`/${festival?.slug}/events/${nextEvent.slug}`}
              >
                <span className="text-sm font-bold flex flex-row items-center">
                  Danach
                  <FiArrowRight className="ml-2" />
                </span>
                <span className="block overflow-hidden text-ellipsis md:whitespace-nowrap text-gray-800 text-sm md:text-md">
                  {nextEvent.title}
                </span>
              </Link>
            )}
          </div>
        )}
      </article>

      <div className="flex-shrink-0 md:w-1/3 md:pl-10">
        <ul className="my-8">
          <li className="px-5 text-xl font-bold text-primary-600 my-8">
            Alle Veranstaltungen
          </li>
          {eventsLoaderData.events.map((event) => {
            const fromDate = parseISO(event.from.toString());

            const thisDayOfYear = getDayOfYear(fromDate);
            const renderDay = currentDayOfYear !== thisDayOfYear;
            currentDayOfYear = thisDayOfYear;

            return (
              <Fragment key={event.id}>
                {renderDay && (
                  <li className="px-4 py-2 rounded-md bg-primary-50 uppercase font-extrabold text-sm text-primary-600">
                    {format(fromDate, "P", { locale: de })}
                  </li>
                )}
                <li>
                  <EventLink event={event} />
                </li>
              </Fragment>
            );
          })}
        </ul>
        {!!isLoggedIn && (
          <ButtonLink to={`/${festival?.slug}/events/new`}>
            Add new event
          </ButtonLink>
        )}
      </div>
    </div>
  );
}
